// Dispatch — Vehicles. Bespoke management page for the owned road fleet
// (vans, trucks, trailers). Vehicles are assets under the hood (asset type
// 'vehicle' + vehicle_details extension) but are excluded from the generic
// asset grid; this page is their home. Capacities feed the Tour Timeline
// warnings: drone capacity sums across a tour's assigned vehicles vs. the
// tour's biggest single show.

import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import useUserStore from "../stores/useUserStore";
import PageLayout from "../components/ui/PageLayout";
import { buttonClass } from "../components/ui/Button";
import Checkbox from "../components/ui/Checkbox";
import { Field, FieldRow, TextArea } from "../components/ui/Field";
import { Select } from "../components/ui/Select";
import Dialog from "../components/ui/Dialog";
import Panel from "../components/ui/Panel";
import LogoSpinner from "../components/ui/LogoSpinner";
import EmptyState from "../components/ui/EmptyState";
import Appear from "../components/ui/Appear";
import FadeIn from "../components/ui/FadeIn";
import Reveal from "../components/ui/Reveal";

export interface VehicleExtension {
	name: string;
	vehicle_class: "van" | "truck" | "trailer";
	drone_capacity: number;
	crew_capacity: number;
	can_tow: boolean;
	license_plate: string | null;
	notes: string | null;
}

export interface Vehicle {
	id: string;
	display_code: string;
	operational_status: string;
	location_bucket: string;
	is_active: boolean;
	_extension: VehicleExtension | null;
	assigned_tours: { id: string; name: string }[];
}

const CLASS_LABELS: Record<string, string> = {
	van: "Van",
	truck: "Truck",
	trailer: "Trailer",
};

const STATUS_STYLES: Record<string, string> = {
	ready: "bg-green-900/30 text-green-300 ring-green-800/60",
	flagged: "bg-yellow-900/30 text-yellow-300 ring-yellow-800/60",
	ooc: "bg-red-900/30 text-red-300 ring-red-800/60",
	checked_out: "bg-blue-900/30 text-blue-300 ring-blue-800/60",
	retired: "bg-surface-container text-on-surface-variant ring-outline-variant",
};

const VehiclesPage: React.FC = () => {
	const api = useApi();
	const toast = useToast();
	const user = useUserStore((state: any) => state.user);
	// Mirrors the backend gate on /api/vehicles writes
	// (operations | producer | manager | admin).
	const canManage = !!(
		user?.isadmin ||
		user?.can_admin ||
		user?.can_operations ||
		user?.can_producer ||
		user?.can_manager
	);

	const [vehicles, setVehicles] = useState<Vehicle[]>([]);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState<Vehicle | null>(null);
	const [showForm, setShowForm] = useState(false);

	const load = useCallback(async () => {
		try {
			const rows = await api.get("/vehicles");
			setVehicles(rows);
		} catch {
			toast.error("Failed to load vehicles");
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const handleRetireToggle = async (vehicle: Vehicle) => {
		const retiring = vehicle.operational_status !== "retired";
		try {
			await api.patch(`/vehicles/${vehicle.id}`, {
				body: { is_active: !retiring },
			});
			toast.success(
				`${vehicle._extension?.name ?? vehicle.display_code} ${retiring ? "retired" : "back in service"}`,
			);
			load();
		} catch {
			toast.error("Failed to update vehicle");
		}
	};

	const active = vehicles.filter((v) => v.operational_status !== "retired");
	const retired = vehicles.filter((v) => v.operational_status === "retired");

	return (
		<PageLayout kind="workspace" className="pt-4 space-y-5">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-2xl font-bold text-primary">Vehicles</h1>
					<p className="text-xs text-on-surface-variant mt-0.5">
						Road fleet for driving tours — capacities feed the Tour
						Timeline warnings
					</p>
				</div>
				{canManage && (
					<button
						onClick={() => {
							setEditing(null);
							setShowForm(true);
						}}
						className={buttonClass("filled")}
					>
						+ New Vehicle
					</button>
				)}
			</div>

			{loading && <LogoSpinner label="Loading vehicles" />}

			{!loading && vehicles.length === 0 && (
				<FadeIn>
					<EmptyState variant="panel">
						No vehicles yet. Add the fleet — vans, trucks, and trailers each
						get their own entry.
					</EmptyState>
				</FadeIn>
			)}

			{!loading && active.length > 0 && (
				<FadeIn className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{active.map((v) => (
						<VehicleCard
							key={v.id}
							vehicle={v}
							canManage={canManage}
							onEdit={() => {
								setEditing(v);
								setShowForm(true);
							}}
							onRetireToggle={() => handleRetireToggle(v)}
						/>
					))}
				</FadeIn>
			)}

			{!loading && retired.length > 0 && (
				<FadeIn>
					<div className="pt-3">
						<div className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold">
							Retired
						</div>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
						{retired.map((v) => (
							<VehicleCard
								key={v.id}
								vehicle={v}
								canManage={canManage}
								onEdit={() => {
									setEditing(v);
									setShowForm(true);
								}}
								onRetireToggle={() => handleRetireToggle(v)}
							/>
						))}
					</div>
				</FadeIn>
			)}

			{showForm && (
				<VehicleFormModal
					vehicle={editing}
					onClose={() => setShowForm(false)}
					onSaved={() => {
						setShowForm(false);
						load();
					}}
				/>
			)}
		</PageLayout>
	);
};

const VehicleCard: React.FC<{
	vehicle: Vehicle;
	canManage: boolean;
	onEdit: () => void;
	onRetireToggle: () => void;
}> = ({ vehicle, canManage, onEdit, onRetireToggle }) => {
	const ext = vehicle._extension;
	const isRetired = vehicle.operational_status === "retired";
	return (
		<Panel
			label={ext?.name ?? vehicle.display_code}
			className="space-y-3"
		>
			<div className="flex justify-between items-start gap-2">
				<div className="min-w-0">
					<div className={`font-semibold text-sm truncate ${isRetired ? "text-on-surface-variant" : "text-white"}`}>
						{ext?.name ?? vehicle.display_code}
					</div>
					<div className="text-on-surface-variant text-[11px] mt-0.5">
						{CLASS_LABELS[ext?.vehicle_class ?? ""] ?? "—"} ·{" "}
						{vehicle.display_code}
						{ext?.license_plate ? ` · ${ext.license_plate}` : ""}
					</div>
				</div>
				<span
					className={`inline-flex px-1.5 py-0.5 rounded ring-1 ring-inset text-[10px] whitespace-nowrap ${
						STATUS_STYLES[vehicle.operational_status] ??
						STATUS_STYLES.ready
					}`}
				>
					{vehicle.operational_status.replace("_", " ")}
				</span>
			</div>

			<div className="flex gap-4 text-xs">
				<div>
					<span className={`font-bold tabular-nums text-base ${isRetired ? "text-on-surface-variant" : "text-primary"}`}>
						{ext?.drone_capacity ?? 0}
					</span>
					<span className="text-on-surface-variant ml-1">drones</span>
				</div>
				<div>
					<span className={`font-bold tabular-nums text-base ${isRetired ? "text-on-surface-variant" : "text-primary"}`}>
						{ext?.crew_capacity ?? 0}
					</span>
					<span className="text-on-surface-variant ml-1">seats</span>
				</div>
				<Appear show={!!ext?.can_tow} className="self-center inline-flex px-1.5 py-0.5 rounded ring-1 ring-inset bg-sky-900/30 text-sky-300 ring-sky-800/60 text-[10px]">
					tows
				</Appear>
			</div>

			<div className="text-[11px] text-on-surface-variant">
				{vehicle.location_bucket}
				{vehicle.assigned_tours.length > 0 && (
					<span className="text-on-surface-variant">
						{" · "}
						{vehicle.assigned_tours.map((t) => t.name).join(", ")}
					</span>
				)}
			</div>

			<Reveal show={!!ext?.notes}>
				<div className="text-[11px] text-on-surface-variant italic truncate" title={ext?.notes}>
					{ext?.notes}
				</div>
			</Reveal>

			{canManage && (
			<div className="flex gap-2 pt-1">
				<button
					onClick={onEdit}
					className={buttonClass("quiet", "sm")}
				>
					Edit
				</button>
				<button
					onClick={onRetireToggle}
					className={buttonClass(isRetired ? "quiet" : "danger-quiet", "sm")}
				>
					{isRetired ? "Un-retire" : "Retire"}
				</button>
			</div>
			)}
		</Panel>
	);
};

const VehicleFormModal: React.FC<{
	vehicle: Vehicle | null;
	onClose: () => void;
	onSaved: () => void;
}> = ({ vehicle, onClose, onSaved }) => {
	const api = useApi();
	const toast = useToast();
	const ext = vehicle?._extension;

	const [name, setName] = useState(ext?.name ?? "");
	const [vehicleClass, setVehicleClass] = useState(ext?.vehicle_class ?? "van");
	const [droneCapacity, setDroneCapacity] = useState(
		String(ext?.drone_capacity ?? 0),
	);
	const [crewCapacity, setCrewCapacity] = useState(
		String(ext?.crew_capacity ?? 0),
	);
	const [canTow, setCanTow] = useState(ext?.can_tow ?? false);
	const [licensePlate, setLicensePlate] = useState(ext?.license_plate ?? "");
	const [notes, setNotes] = useState(ext?.notes ?? "");
	const [saving, setSaving] = useState(false);

	// Compared with what the form opened with (the record's values, or the new-vehicle defaults).
	const dirty =
		name !== (ext?.name ?? "") ||
		vehicleClass !== (ext?.vehicle_class ?? "van") ||
		droneCapacity !== String(ext?.drone_capacity ?? 0) ||
		crewCapacity !== String(ext?.crew_capacity ?? 0) ||
		canTow !== (ext?.can_tow ?? false) ||
		licensePlate !== (ext?.license_plate ?? "") ||
		notes !== (ext?.notes ?? "");

	const handleSubmit = async () => {
		if (!name.trim()) {
			toast.error("Name is required");
			return;
		}
		setSaving(true);
		const extension = {
			name: name.trim(),
			vehicle_class: vehicleClass,
			drone_capacity: parseInt(droneCapacity, 10) || 0,
			crew_capacity: parseInt(crewCapacity, 10) || 0,
			can_tow: canTow,
			license_plate: licensePlate.trim() || null,
			notes: notes.trim() || null,
		};
		try {
			if (vehicle) {
				await api.patch(`/vehicles/${vehicle.id}`, { body: { extension } });
			} else {
				await api.post("/vehicles", { body: { extension } });
			}
			toast.success(vehicle ? "Vehicle updated" : "Vehicle added");
			onSaved();
		} catch {
			toast.error("Failed to save vehicle");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open
			onClose={onClose}
			title={vehicle ? `Edit ${ext?.name ?? "Vehicle"}` : "New Vehicle"}
			size="md"
			busy={saving}
			dirty={dirty}
			onSubmit={() => handleSubmit()}
			footer={
				<>
					<button
						type="button"
						onClick={onClose}
						className={buttonClass("quiet")}
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={saving}
						className={buttonClass("filled")}
					>
						{saving ? "Saving…" : vehicle ? "Save" : "Add Vehicle"}
					</button>
				</>
			}
		>
			<div className="space-y-3">
				<FieldRow label="Name">
					<Field
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Big Van"
						autoFocus
					/>
				</FieldRow>
				<div className="grid grid-cols-2 gap-3">
					<FieldRow label="Class">
						<Select
							label="Class"
							value={vehicleClass}
							onChange={(key) => setVehicleClass(key as VehicleExtension["vehicle_class"])}
							options={[
								{ key: "van", label: "Van" },
								{ key: "truck", label: "Truck" },
								{ key: "trailer", label: "Trailer" },
							]}
						/>
					</FieldRow>
					<FieldRow label="License plate">
						<Field
							value={licensePlate}
							onChange={(e) => setLicensePlate(e.target.value)}
						/>
					</FieldRow>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<FieldRow label="Drone capacity">
						<Field
							type="number"
							min="0"
							value={droneCapacity}
							onChange={(e) => setDroneCapacity(e.target.value)}
						/>
					</FieldRow>
					<FieldRow label="Crew seats">
						<Field
							type="number"
							min="0"
							value={crewCapacity}
							onChange={(e) => setCrewCapacity(e.target.value)}
						/>
					</FieldRow>
				</div>
				<Checkbox
					checked={canTow}
					onChange={(e) => setCanTow(e.target.checked)}
					label="Can tow a trailer"
				/>
				<FieldRow label="Notes">
					<TextArea
						rows={2}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
				</FieldRow>
			</div>
		</Dialog>
	);
};

export default VehiclesPage;
